import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  orderBy,
  limit,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";

/**
 * Migrates CYLINDER drafts from:
 *   returns_draft/{draftId}
 * into:
 *   returns_v2/{newId}
 *
 * Adds universal search fields on the NEW returns_v2 doc:
 *   - searchText: string (debug / preview)
 *   - searchTokens: string[] (queryable with array-contains)
 *
 * Does NOT delete from old collection.
 * Copies storage.folderId/folderUrl if present.
 */

const SRC_COL = "returns_draft";
const DEST_COL = "returns_v2";

// ---------- small helpers ----------
function safeId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "id_" + Math.random().toString(16).slice(2) + "_" + Date.now();
}

// ---------- search helpers (REPLACEMENT) ----------
function norm(v) {
  return String(v || "").trim().toLowerCase();
}

function stripNonAlnumToSpaces(s) {
  return norm(s).replace(/[^a-z0-9]+/g, " ").trim();
}

function stripDigits(s) {
  return String(s || "").replace(/\D+/g, "");
}

// keep this small; these are the ones that cause token explosion and add zero value
const STOPWORDS = new Set([
  "a","an","the","and","or","to","of","in","on","for","with","at","by","from","as","is","it","be","was","were",
  "this","that","these","those","are","am","i","you","we","they","he","she","my","your","our","their"
]);

function addWordTokens(set, value) {
  const s = stripNonAlnumToSpaces(value);
  if (!s) return;

  // whole phrase token (useful when user pastes “rn 000650”)
  set.add(s);

  // individual words (but drop stopwords + 1-char junk)
  for (const w of s.split(/\s+/)) {
    if (!w || w.length < 2) continue;
    if (STOPWORDS.has(w)) continue;
    set.add(w);
  }
}

function addPrefixes(set, raw, minLen = 3, maxLen = 12) {
  // prefixes are ONLY for things users partial-type (serial/RN/SRET/model code)
  const s = norm(raw).replace(/\s+/g, "");
  if (!s) return;
  const capped = s.slice(0, maxLen);
  for (let i = minLen; i <= capped.length; i++) {
    set.add(capped.slice(0, i));
  }
}

// Returned parts logic (same as your original)
const INDIRECT_PARTS = ["Inlet Group", "Tundish", "Expansion Vessel", "2 Port Valve", "Dual Stat", "Immersion"];
const DIRECT_PARTS = ["Inlet Group", "Tundish", "Expansion Vessel", "Smart Immersion", "Immersion"];
function getSystemList(systemType) {
  if (systemType === "indirect") return INDIRECT_PARTS;
  if (systemType === "direct") return DIRECT_PARTS;
  return [];
}
function buildReturnedParts(parts) {
  if (!parts) return [];
  if (parts.noPartsReturned) return [];
  const base = getSystemList(parts.systemType);
  if (parts.allPartsReturned) return [...base];
  const chosenBase = Object.entries(parts.partialBaseSelection || {})
    .filter(([, v]) => !!v)
    .map(([name]) => name);
  const extra = (parts.additionalParts || []).map((x) => String(x).trim()).filter(Boolean);
  return [...chosenBase, ...extra];
}

// Lightweight scoring (unchanged)
function computeProgressCyl({ form, parts, leaks, reasonOptions = [] }) {
  try {
    let s = 0;
    if (form?.returnsNoteNumber) s += 12;
    if (form?.serialNumber) s += 12;
    if (form?.cylinderModel) s += 12;

    if (form?.reasonCode) s += 12;
    const rMeta = reasonOptions.find((x) => x.code === form?.reasonCode);
    if (!rMeta?.requiresDefect || form?.reasonDefectCode) s += 12;

    if (form?.faultFoundCode) s += 12;
    const ffMeta = reasonOptions.find((x) => x.code === form?.faultFoundCode);
    if (!ffMeta?.requiresDefect || form?.faultDefectCode) s += 12;

    const picked = [parts?.noPartsReturned, parts?.allPartsReturned, parts?.partialReturn].filter(Boolean).length;
    if (picked > 0) s += 8;

    if (!leaks?.other || (leaks?.other && leaks?.otherComments)) s += 8;

    return Math.min(100, s);
  } catch {
    return 0;
  }
}



/**
 * MUCH tighter token strategy:
 * - words + phrase tokens for general fields
 * - prefixes ONLY for RN/serial/SRET/model
 * - NO prefixes for notes
 * - hard caps so you don’t blow doc size
 */
function buildSearchIndexForV2(v2, { includeNotes = false } = {}) {
  const tokens = new Set();

  const type = norm(v2?.type);     // "cyl" | "parts"
  const status = norm(v2?.status); // "in_progress" etc (or whatever you store)

  const f = v2?.cyl?.form || v2?.parts?.form || {};
  const returnedParts = v2?.cyl?.returnedParts || [];

  // ---- Canonical tokens (no word splitting)
  if (type) tokens.add(`type:${type}`);
  if (status) tokens.add(`status:${status}`);

  // ---- RN / Serial / SRET (exact + digits + prefixes)
  const rn = norm(f.returnsNoteNumber);
  const rnDigits = stripDigits(rn);
  if (rn) tokens.add(rn); // "rn-000650" normalized
  if (rnDigits) {
    tokens.add(rnDigits);         // "000650"
    tokens.add(`rn${rnDigits}`);  // "rn000650"
    addPrefixes(tokens, rnDigits, 4, 10); // start at 4 to reduce junk ("000" is useless)
  }

  const serial = norm(f.serialNumber);
  const serialDigits = stripDigits(serial);
  if (serial) tokens.add(serial);
  if (serialDigits) {
    tokens.add(serialDigits);
    addPrefixes(tokens, serialDigits, 4, 12);
  }

  const sret = norm(f.sretNumber);
  const sretDigits = stripDigits(sret);
  if (sret) tokens.add(sret);
  if (sretDigits) {
    tokens.add(sretDigits);
    addPrefixes(tokens, sretDigits, 4, 12);
  }

  // ---- Model / partCode (words + one compact token + prefixes on compact only)
  const model = stripNonAlnumToSpaces(f.cylinderModel);
  if (model) {
    // words (but not phrase)
    for (const w of model.split(/\s+/)) {
      if (!w || w.length < 2) continue;
      if (STOPWORDS.has(w)) continue;
      tokens.add(w);
    }
    // compact model code for partial typing (hpsk6191ultimate)
    const modelCompact = norm(f.cylinderModel).replace(/[^a-z0-9]+/g, "");
    if (modelCompact) {
      tokens.add(modelCompact);
      addPrefixes(tokens, modelCompact, 4, 14);
    }
  }

  const partCodeCompact = norm(f.partCode).replace(/[^a-z0-9]+/g, "");
  if (partCodeCompact) {
    tokens.add(partCodeCompact);
    addPrefixes(tokens, partCodeCompact, 3, 14);
  }

  // ---- Capacity (minimal)
  const cap = stripNonAlnumToSpaces(f.capacity);
  if (cap) {
    for (const w of cap.split(/\s+/)) {
      if (!w || w.length < 2) continue;
      if (STOPWORDS.has(w)) continue;
      tokens.add(w); // "250" "ltr"
    }
    // also "250ltr" compact
    const capCompact = norm(f.capacity).replace(/[^a-z0-9]+/g, "");
    if (capCompact) tokens.add(capCompact);
  }

  // ---- Codes (exact tokens only)
  ["reasonCode","reasonDefectCode","faultFoundCode","faultDefectCode","reportedCode"].forEach((k) => {
    const val = norm(f[k]);
    if (val) tokens.add(val);
  });

  // ---- returned parts (words only)
  if (returnedParts.length) {
    const rp = stripNonAlnumToSpaces(returnedParts.join(" "));
    for (const w of rp.split(/\s+/)) {
      if (!w || w.length < 2) continue;
      if (STOPWORDS.has(w)) continue;
      tokens.add(w);
    }
  }

  // ---- notes (OFF by default)
  if (includeNotes) {
    const notes = stripNonAlnumToSpaces(f.internalNotes).slice(0, 300);
    if (notes) {
      // only take first N significant words to stop token spam
      const words = notes.split(/\s+/).filter(w => w && w.length >= 3 && !STOPWORDS.has(w));
      words.slice(0, 12).forEach(w => tokens.add(w));
    }
  }

  const searchTokens = Array.from(tokens)
    .filter(Boolean)
    .slice(0, 250); // hard cap

  const searchText = searchTokens.join(" • ");
  return { searchTokens, searchText };
}



export default function ReturnsDraftMigratorCylToV2() {
  const [loading, setLoading] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [previewId, setPreviewId] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null);

  const [copyBusy, setCopyBusy] = useState(false);
  const [log, setLog] = useState([]);
  const [err, setErr] = useState("");

  const [reasonOptions, setReasonOptions] = useState([]);

  const selectedCount = selectedIds.size;
  const addLog = (line) => setLog((p) => [line, ...p].slice(0, 200));

  // optional reasons load (for progress scoring)
  useEffect(() => {
    (async () => {
      try {
        const rs = await getDocs(query(collection(db, "return_reasons")));
        setReasonOptions(rs.docs.map((d) => d.data()).filter(Boolean));
      } catch (e) {
        console.warn("Could not load return_reasons; progress will be approximate.", e);
      }
    })();
  }, []);

  const loadList = async () => {
    setErr("");
    setLoading(true);
    setPreviewId(null);
    setPreviewDoc(null);
    try {
      const qy = query(collection(db, SRC_COL), orderBy("updatedAt", "desc"), limit(200));
      const snap = await getDocs(qy);
      const rows = snap.docs.map((d) => {
        const data = d.data() || {};
        const form = data.form || {};
        return {
          id: d.id,
          updatedAt: data.updatedAt?.toDate?.()?.toISOString?.() || "",
          status: data.status || "",
          rn: form.returnsNoteNumber || "",
          serial: form.serialNumber || "",
          model: form.cylinderModel || "",
          hasStorage: !!data.storage?.folderId,
          raw: data,
        };
      });
      setDrafts(rows);
      addLog(`Loaded ${rows.length} drafts from ${SRC_COL}.`);
    } catch (e) {
      console.error(e);
      setErr(e.message || "Failed to load drafts.");
    } finally {
      setLoading(false);
    }
  };

  const loadPreview = async (id) => {
    setErr("");
    setPreviewId(id);
    setPreviewDoc(null);
    try {
      const snap = await getDoc(doc(db, SRC_COL, id));
      if (!snap.exists()) {
        setErr("Preview failed: draft no longer exists.");
        return;
      }
      setPreviewDoc({ id, ...snap.data() });
    } catch (e) {
      console.error(e);
      setErr(e.message || "Preview failed.");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(drafts.map((d) => d.id)));
  const clearAll = () => setSelectedIds(new Set());

  // Build V2 payload from old draft doc + ADD SEARCH FIELDS
  const buildV2FromDraft = (draftId, d) => {
    const form = d.form || {};
    const parts = d.parts || {};
    const leaks = d.leaks || {};
    const cylCheck = d.cylCheck || {};

    const status = d.status || "new";
    const returnedParts = buildReturnedParts(parts);
    const progress = computeProgressCyl({ form, parts, leaks, reasonOptions });

    const createdAt = d.createdAt || serverTimestamp();
    const updatedAt = serverTimestamp();

    // base v2 doc
    const v2 = {
      type: "cyl",
      status,
      statusUpdatedAt: d.statusUpdatedAt || null,
      createdAt,
      updatedAt,
      submittedAt: d.submittedAt || null,

      // ✅ NEW: default archive flags for ALL migrated docs
      archive: false,
      archivedAt: null,

      progress,
      source: d.source || "pthelpdesk",
      version: 1,

      migratedFrom: {
        collection: SRC_COL,
        draftId,
        migratedAt: serverTimestamp(),
      },

      cyl: {
        form,
        parts,
        leaks,
        cylCheck,
        returnedParts,
      },
    };


    // copy storage
    if (d.storage?.folderId) {
      v2.storage = {
        provider: d.storage.provider || "sharepoint",
        folderId: d.storage.folderId,
        folderUrl: d.storage.folderUrl || null,
      };
    }

    // ✅ ADD SEARCH FIELDS HERE (this is what you were missing)
    const { searchTokens, searchText } = buildSearchIndexForV2(v2);
    v2.searchTokens = searchTokens;
    v2.searchText = searchText;
    v2.searchUpdatedAt = serverTimestamp();

    return v2;
  };

  const copySelected = async () => {
    if (selectedCount === 0) {
      alert("Select at least one draft first.");
      return;
    }
    if (!window.confirm(`Copy ${selectedCount} draft(s) into ${DEST_COL}? (No deletes, just copies)`)) return;

    setCopyBusy(true);
    setErr("");
    try {
      const ids = Array.from(selectedIds);

      for (let i = 0; i < ids.length; i++) {
        const draftId = ids[i];
        addLog(`(${i + 1}/${ids.length}) Reading draft ${draftId}...`);

        const snap = await getDoc(doc(db, SRC_COL, draftId));
        if (!snap.exists()) {
          addLog(`⚠️ Draft missing: ${draftId} (skipped)`);
          continue;
        }

        const draftData = snap.data() || {};
        const newId = safeId();
        const payload = buildV2FromDraft(draftId, draftData);

        // Full overwrite write for the NEW doc
        await setDoc(doc(db, DEST_COL, newId), payload);
        addLog(`✅ Copied ${draftId} -> ${DEST_COL}/${newId} (with search fields)`);
      }

      addLog(`DONE: copied ${selectedCount} draft(s).`);
      alert("Copy complete.");
      clearAll();
    } catch (e) {
      console.error(e);
      setErr(e.message || "Copy failed.");
    } finally {
      setCopyBusy(false);
    }
  };

  const previewSummary = useMemo(() => {
    if (!previewDoc) return null;
    const f = previewDoc.form || {};
    return {
      returnsNoteNumber: f.returnsNoteNumber || "",
      serialNumber: f.serialNumber || "",
      cylinderModel: f.cylinderModel || "",
      status: previewDoc.status || "",
      hasStorage: !!previewDoc.storage?.folderId,
      updatedAt: previewDoc.updatedAt?.toDate?.()?.toISOString?.() || "",
    };
  }, [previewDoc]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h1 className="text-3xl font-bold text-gray-900">Migrate Cylinder Drafts → returns_v2</h1>

        <button
          type="button"
          onClick={loadList}
          disabled={loading || copyBusy}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
        >
          {loading ? "Loading…" : "Load drafts"}
        </button>

        <button
          type="button"
          onClick={selectAll}
          disabled={drafts.length === 0 || copyBusy}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Select all
        </button>

        <button
          type="button"
          onClick={clearAll}
          disabled={selectedCount === 0 || copyBusy}
          className="px-3 py-2 rounded-md border border-gray-300 hover:bg-gray-50"
        >
          Clear
        </button>

        <button
          type="button"
          onClick={copySelected}
          disabled={selectedCount === 0 || copyBusy}
          className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {copyBusy ? "Copying…" : `Copy selected (${selectedCount})`}
        </button>

        <div className="ml-auto text-sm text-gray-600">
          Source: <b>{SRC_COL}</b> → Destination: <b>{DEST_COL}</b>
        </div>
      </div>

      {err && <div className="mb-4 text-sm text-red-600">{err}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-6">
        {/* LIST */}
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Loaded: <b>{drafts.length}</b> · Selected: <b>{selectedCount}</b>
            </div>
            <div className="text-xs text-gray-500">Click a row to preview</div>
          </div>

          {drafts.length === 0 ? (
            <div className="p-6 text-sm text-gray-600">
              Click <b>Load drafts</b> to fetch the latest cylinder drafts.
            </div>
          ) : (
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead className="text-left bg-white sticky top-0">
                  <tr className="border-b">
                    <th className="p-3 w-10">✓</th>
                    <th className="p-3">RN</th>
                    <th className="p-3">Serial</th>
                    <th className="p-3">Model</th>
                    <th className="p-3">Status</th>
                    <th className="p-3">Photos</th>
                    <th className="p-3">Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {drafts.map((d) => {
                    const checked = selectedIds.has(d.id);
                    const isPreview = previewId === d.id;
                    return (
                      <tr
                        key={d.id}
                        className={`border-b hover:bg-gray-50 cursor-pointer ${isPreview ? "bg-indigo-50" : ""}`}
                        onClick={() => loadPreview(d.id)}
                      >
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSelect(d.id)}
                            className="h-4 w-4"
                          />
                        </td>
                        <td className="p-3 font-medium">{d.rn || <span className="text-gray-400">—</span>}</td>
                        <td className="p-3">{d.serial || <span className="text-gray-400">—</span>}</td>
                        <td className="p-3">{d.model || <span className="text-gray-400">—</span>}</td>
                        <td className="p-3">{d.status || <span className="text-gray-400">—</span>}</td>
                        <td className="p-3">{d.hasStorage ? "Yes" : "No"}</td>
                        <td className="p-3 text-xs text-gray-500">{d.updatedAt ? d.updatedAt.slice(0, 19) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* PREVIEW + LOG */}
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold">Preview</h2>
              {previewId && (
                <span className="text-xs text-gray-500">
                  Draft: <b>{previewId.slice(-8)}</b>
                </span>
              )}
            </div>

            {!previewDoc ? (
              <div className="text-sm text-gray-600">Select a draft row to preview its data.</div>
            ) : (
              <>
                <div className="text-sm text-gray-700 space-y-1 mb-3">
                  <div><b>RN:</b> {previewSummary.returnsNoteNumber || "—"}</div>
                  <div><b>Serial:</b> {previewSummary.serialNumber || "—"}</div>
                  <div><b>Model:</b> {previewSummary.cylinderModel || "—"}</div>
                  <div><b>Status:</b> {previewSummary.status || "—"}</div>
                  <div><b>Photos folder:</b> {previewSummary.hasStorage ? "Yes" : "No"}</div>
                </div>

                <details className="text-sm">
                  <summary className="cursor-pointer text-indigo-700">Show raw draft JSON</summary>
                  <pre className="mt-2 p-3 bg-gray-50 border rounded overflow-auto text-xs">
                    {JSON.stringify(previewDoc, null, 2)}
                  </pre>
                </details>

                <details className="text-sm mt-3">
                  <summary className="cursor-pointer text-indigo-700">Show converted returns_v2 JSON (preview)</summary>
                  <pre className="mt-2 p-3 bg-gray-50 border rounded overflow-auto text-xs">
                    {JSON.stringify(buildV2FromDraft(previewId, previewDoc), null, 2)}
                  </pre>
                </details>
              </>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <h2 className="text-lg font-semibold mb-3">Log</h2>
            {log.length === 0 ? (
              <div className="text-sm text-gray-600">Actions will appear here.</div>
            ) : (
              <div className="max-h-80 overflow-auto text-xs space-y-1">
                {log.map((l, i) => (
                  <div key={i} className="font-mono text-gray-700">{l}</div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6 text-xs text-gray-500">
        Notes:
        <ul className="list-disc pl-5 mt-1 space-y-1">
          <li>This now writes <b>searchTokens</b> + <b>searchText</b> into every migrated <b>returns_v2</b> doc.</li>
          <li>Old drafts remain untouched in <b>{SRC_COL}</b>.</li>
          <li>Each migrated record includes a <b>migratedFrom</b> block for traceability.</li>
        </ul>
      </div>
    </div>
  );
}
